import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
} from '@angular/core';

/**
 * Field-to-field navigation for questionnaire pages.
 *
 * The questionnaire is NOT wrapped in a native <form>, and each question type
 * is rendered by its own component. As a result the browser/mobile keyboard
 * has no notion of "the next field", so every input shows the default Enter/Go
 * key and there is no Enter-to-advance on desktop.
 *
 * This directive, applied to the `.page` flex container, restores that flow:
 *  - sets `enterkeyhint="next"` on every text input except the last one on the
 *    page (which gets `enterkeyhint="done"`), so mobile keyboards render a
 *    "Next" button instead of "Go"/"Enter" until the final field;
 *  - on Enter, moves focus to the next input in *visual* order (the page uses
 *    flexbox `order`, so DOM order is not necessarily the visual order),
 *    dismissing the keyboard once the last field is reached.
 *
 * Textareas (LONG_TEXT) are intentionally left out so Enter keeps inserting
 * line breaks. Inputs that live inside overlay panels (mat-select search,
 * intl-tel country search, autocomplete) are filter inputs and are skipped.
 */
@Directive({
  selector: '[appQuestionnaireFormFlow]',
})
export class FormFlowNavigationDirective implements AfterViewInit, OnDestroy {
  /** Text-entry inputs only — checkboxes/radios/files/etc. are not part of the flow. */
  private readonly focusableSelector =
    'input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=reset]):not([type=file]):not([type=range]):not([type=color])';

  /** Inputs inside these are search/filter fields, not questionnaire answers. */
  private readonly excludeWithin =
    '.country-search, .mat-select-search, mat-autocomplete, .pac-container, .iti__country-list';

  /** Types where setSelectionRange is supported (used to drop the caret at the end). */
  private readonly caretAwareTypes = /^(text|search|tel|url|password|email)$/;

  private observer?: MutationObserver;
  private rafHandle = 0;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.scheduleRefresh();

    // Questions appear/disappear via hide_if and dynamic inputs (e.g. the phone
    // field) mount after init, so re-evaluate the enterkeyhint chain whenever
    // the page subtree changes. Run outside Angular — this only touches
    // attributes and must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.scheduleRefresh());
      this.observer.observe(this.host.nativeElement, {
        childList: true,
        subtree: true,
      });
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
    }
  }

  @HostListener('keydown.enter', ['$event'])
  onEnter(event: Event): void {
    const target = event.target;
    // Only plain inputs advance. Textareas keep Enter for newlines; everything
    // else (selects, buttons) keeps its native behavior.
    if (!(target instanceof HTMLInputElement)) {
      return;
    }
    if (target.closest(this.excludeWithin)) {
      return;
    }

    const inputs = this.getOrderedInputs();
    const index = inputs.indexOf(target);
    if (index === -1) {
      return;
    }

    // Prevent any implicit submit / scroll-into-view side effects.
    event.preventDefault();

    const next = inputs[index + 1];
    if (next) {
      next.focus();
      this.placeCaretAtEnd(next);
    } else {
      // Last field: dismiss the virtual keyboard instead of doing nothing.
      target.blur();
    }
  }

  /** Coalesce bursts of mutations into a single refresh on the next frame. */
  private scheduleRefresh(): void {
    if (this.rafHandle) {
      return;
    }
    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = 0;
      this.refresh();
    });
  }

  /** Set `enterkeyhint` on each input based on its position in the visual order. */
  private refresh(): void {
    const inputs = this.getOrderedInputs();
    const lastIndex = inputs.length - 1;
    inputs.forEach((input, index) => {
      input.setAttribute('enterkeyhint', index === lastIndex ? 'done' : 'next');
    });
  }

  /** Visible, enabled text inputs sorted by visual (flex `order`) then DOM order. */
  private getOrderedInputs(): HTMLInputElement[] {
    const host = this.host.nativeElement;
    const usesFlexOrder = /flex|grid/.test(getComputedStyle(host).display);

    return Array.from(
      host.querySelectorAll<HTMLInputElement>(this.focusableSelector)
    )
      .filter((el) => this.isParticipating(el))
      .sort((a, b) => {
        if (usesFlexOrder) {
          const orderDelta = this.visualOrder(a) - this.visualOrder(b);
          if (orderDelta !== 0) {
            return orderDelta;
          }
        }
        // Same flex order (or no flex): fall back to document order.
        const relation = a.compareDocumentPosition(b);
        return relation & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });
  }

  private isParticipating(el: HTMLInputElement): boolean {
    if (el.disabled || el.readOnly) {
      return false;
    }
    if (el.closest(this.excludeWithin)) {
      return false;
    }
    // offsetParent is null for display:none / hidden elements (covers hide_if
    // questions that are still in the DOM during teardown).
    return el.offsetParent !== null;
  }

  /**
   * The flex `order` is applied to the direct child of `.page` (the
   * `<app-question>` element). Walk up from the input to that child and read
   * its computed order.
   */
  private visualOrder(input: HTMLInputElement): number {
    const host = this.host.nativeElement;
    let node: HTMLElement | null = input;
    while (node && node.parentElement && node.parentElement !== host) {
      node = node.parentElement;
    }
    if (!node) {
      return 0;
    }
    const parsed = parseInt(getComputedStyle(node).order, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private placeCaretAtEnd(input: HTMLInputElement): void {
    if (!this.caretAwareTypes.test(input.type)) {
      return;
    }
    try {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    } catch {
      // Some masked inputs reject setSelectionRange — focus alone is enough.
    }
  }
}
